import axios from "axios";
import { captureException } from "./posthog";

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isStandardApiSuccessResponse = (payload) => {
  if (!isObject(payload)) return false;
  if (!Object.prototype.hasOwnProperty.call(payload, "data")) return false;

  const keys = Object.keys(payload);
  return keys.every((key) => key === "data" || key === "meta");
};

const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_LOCAL_API_URL;
  if (!apiUrl) {
    console.warn(
      "REACT_APP_LOCAL_API_URL is not set. Using default: http://localhost:8000/api"
    );
    return "http://192.168.68.100:8000/api";
  }
  return apiUrl;
};

// Create axios instance with default config
const createApiClient = (endpoint) => {
  const baseUrl = getApiBaseUrl();
  const endpointPath = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  const fullBaseURL = `${baseUrl}${endpointPath}`;

  const client = axios.create({
    baseURL: fullBaseURL,
    timeout: 10000,
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      Accept: "application/json; charset=utf-8",
    },
  });

  // Request interceptor to add auth token
  client.interceptors.request.use(
    (config) => {
      // Get token from localStorage or Supabase session
      const userData = localStorage.getItem("userData");
      if (userData) {
        try {
          const { token } = JSON.parse(userData);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error("Error parsing user data:", error);
          captureException(error, {
            source: "apiClient.requestInterceptor",
            reason: "parse-userData",
          });
        }
      }

      // Also try to get token from Supabase session
      const supabaseSession = localStorage.getItem(
        "sb-bkhaviccwhpouwmuydef-auth-token"
      );
      if (supabaseSession) {
        try {
          const { access_token } = JSON.parse(supabaseSession);
          if (access_token && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${access_token}`;
          }
        } catch (error) {
          console.error("Error parsing Supabase session:", error);
          captureException(error, {
            source: "apiClient.requestInterceptor",
            reason: "parse-supabaseSession",
          });
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle auth errors only
  client.interceptors.response.use(
    (response) => {
      const payload = response?.data;
      if (isStandardApiSuccessResponse(payload)) {
        response.data = payload.data;
        if (payload.meta !== undefined) {
          response.meta = payload.meta;
        }
      }
      return response;
    },
    (error) => {
      captureException(error, {
        source: "api",
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        endpoint: fullBaseURL,
      });
      if (error.response?.status === 401) {
        // Clear invalid tokens
        localStorage.removeItem("userData");
        localStorage.removeItem("sb-bkhaviccwhpouwmuydef-auth-token");

        // Redirect to login if not already there
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// Create API clients for different endpoints
export const createUserApiClient = () => createApiClient("users");

export const createCustomerApiClient = () => createApiClient("organisations");

export const createFacilityApiClient = () => createApiClient("facilities");

export const createGroupApiClient = () => createApiClient("usergroups");

export const createDocumentApiClient = () => createApiClient("documents");

export const createAuthApiClient = () => createApiClient("auth");

export const createMedApiClient = () => createApiClient("medicine");

export const createServiceApiClient = () => createApiClient("service");

export const createContinuousLocationApiClient = () =>
  createApiClient("continuous-location");

// Generic function to create API client for any endpoint
export const createApiClientForEndpoint = (endpoint) => createApiClient(endpoint);

// Export the base function for custom use
export { createApiClient };
